/* eslint-disable @typescript-eslint/no-explicit-any */
import type Highcharts from "highcharts";

declare module "highcharts" {
  interface Chart {
    setUpScrolling: () => void;
  }

  interface Pointer {
    chartPosition: any;
  }
}

// Highcharts uses SVGElement#setAttribute("style", ...) liberately, which breacks CSP "style-src 'self'"
// We could use chart.styledMode https://www.highcharts.com/docs/chart-design-and-style/style-by-css
// but it really lags behind for lines and markers styling in line charts
// We instead override the function with this idea:
// https://stackoverflow.com/a/57633533/264514
export default function fixCSPIssues(H: typeof Highcharts) {
  H.wrap(
    SVGElement.prototype,
    "setAttribute",
    function (
      this: SVGElement,
      proceed: typeof SVGElement.prototype.setAttribute,
      ...args: Parameters<typeof SVGElement.prototype.setAttribute>
    ) {
      if (args[0] !== "style") return proceed.apply(this, args);

      for (const cssRuleString of args[1].split(";").filter(Boolean)) {
        const [ruleKey, ruleValue] = cssRuleString.split(":");
        if (!ruleKey || !ruleValue)
          throw new Error("Missing ruleKey or ruleValue in " + cssRuleString);
        switch (ruleKey.trim()) {
          case "font-family":
            this.style.fontFamily = ruleValue;
            break;
          case "font-size":
            this.style.fontSize = ruleValue;
            break;
          case "font-weight":
            this.style.fontWeight = ruleValue;
            break;
          case "fill":
            this.style.fill = ruleValue;
            break;
          case "cursor":
            this.style.cursor = ruleValue;
            break;
          case "color":
            this.style.color = ruleValue;
            break;
          case "display":
            this.style.display = ruleValue;
            break;
          case "pointer-events":
            this.style.pointerEvents = ruleValue;
            break;
          case "white-space":
            this.style.whiteSpace = ruleValue;
            break;
          case "opacity":
            this.style.opacity = ruleValue;
            break;
          case "mixed-blend-mode":
            (this.style as any).mixedBlendMode = ruleValue;
            break;
          case "padding-top":
            this.style.paddingTop = ruleValue;
            break;
          case "padding-bottom":
            this.style.paddingBottom = ruleValue;
            break;
          case "border-width":
            this.style.borderWidth = ruleValue;
            break;
          case "height":
            this.style.height = ruleValue;
            break;
          default:
            break;
        }
      }
    },
  );

  // HEY HERE, PLEASE README
  // Firefox CSP does not allow moving dom trees with style attributes inside (Chrome doesn't mind)
  // It prints a warning and removes style attributes contents
  // Below are several ugly best-effort hacks to fix the situation

  // fix HTMLElement#appendChild(SVGElement) calls for Firefox CSP
  // - remove style on SVGElement
  // - appendChild(SVGElement)
  // - move back style on SVGElement
  H.wrap(
    HTMLElement.prototype,
    "appendChild",
    function (
      this: HTMLElement,
      proceed: typeof HTMLElement.prototype.appendChild,
      ...args: Parameters<typeof HTMLElement.prototype.appendChild>
    ) {
      const newChild = args[0] as Text | HTMLElement | SVGElement;
      if (newChild instanceof Text) {
        return proceed.apply(this, args);
      }
      if (newChild instanceof HTMLElement) {
        return proceed.apply(this, args);
      }
      if (newChild instanceof SVGElement) {
        const childStyle = newChild.getAttribute("style");
        if (childStyle === null) return proceed.apply(this, args);
        newChild.removeAttribute("style");
        const returned = proceed.apply(this, args);
        newChild.setAttribute("style", childStyle);
        return returned;
      }
      return proceed.apply(this, args);
    },
  );

  // fix SVGElement#insertBefore(SVGElement) for Firefox CSP
  // - remove style on SVGElement
  // - appendChild(SVGElement)
  // - move back style on SVGElement
  H.wrap(
    SVGElement.prototype,
    "insertBefore",
    function (
      this: SVGElement,
      proceed: typeof SVGElement.prototype.insertBefore,
      ...args: Parameters<typeof SVGElement.prototype.insertBefore>
    ) {
      const newChild = args[0] as Text | HTMLElement | SVGElement;
      if (!(newChild instanceof SVGElement)) return proceed.apply(this, args);
      const childStyle = newChild.getAttribute("style");
      if (childStyle === null) return proceed.apply(this, args);

      newChild.removeAttribute("style");
      const returned = proceed.apply(this, args);
      newChild.setAttribute("style", childStyle);
      return returned;
    },
  );

  // recursively collect and remove all styles in a DOM tree.
  function collectAndRemoveStyles(domNode: ChildNode): string[] {
    if (domNode instanceof Text) return [];
    const element = domNode as HTMLElement | SVGElement;
    const style = element.getAttribute("style");
    if (style !== null) {
      element.removeAttribute("style");
    }
    const childStyles = Array.from(element.childNodes).flatMap(collectAndRemoveStyles);
    if (style) {
      return [style, ...childStyles];
    } else {
      return childStyles;
    }
  }

  function getScrollParent(element: HTMLElement) {
    let style = window.getComputedStyle(element);

    for (let parent = element; (parent = parent.parentElement as HTMLElement); ) {
      style = window.getComputedStyle(parent);
      if (style.overflowX === "auto") {
        return parent;
      }
    }

    throw new Error("Overflow parent not found");
  }

  // override Highcharts moveFixedElements (called for scrolling charts)
  // - before moving elements to the fixed wrapper, we collect and remove descendants styles
  // - move them to the fixed wrapper
  // - put back all the style on the parent element
  // this is highly approximative, but it works in practice...
  // https://github.com/highcharts/highcharts/blob/af5fdee7fecda154d0bec897788f17d6ee1aa477/ts/parts/ScrollablePlotArea.ts#L286
  H.Chart.prototype.moveFixedElements = function (): void {
    const container = this.container;
    const fixedRenderer = (this as any).fixedRenderer;
    const fixedSelectors = [
      ".highcharts-contextbutton",
      ".highcharts-credits",
      ".highcharts-legend",
      ".highcharts-legend-checkbox",
      ".highcharts-navigator-series",
      ".highcharts-navigator-xaxis",
      ".highcharts-navigator-yaxis",
      ".highcharts-navigator",
      ".highcharts-reset-zoom",
      ".highcharts-scrollbar",
      ".highcharts-subtitle",
      ".highcharts-title",
      ".highcharts-yaxis",
    ];
    let axisClass;

    if (Number(this.scrollablePixelsX) && !this.inverted) {
      axisClass = ".highcharts-yaxis";
    } else if (Number(this.scrollablePixelsX) && this.inverted) {
      axisClass = ".highcharts-xaxis";
    } else if (Number(this.scrollablePixelsY) && !this.inverted) {
      axisClass = ".highcharts-xaxis";
    } else if (Number(this.scrollablePixelsY) && this.inverted) {
      axisClass = ".highcharts-yaxis";
    }

    fixedSelectors.push(axisClass as any, axisClass + "-labels");
    const parent = fixedRenderer.box;
    for (const className of fixedSelectors) {
      for (const elem of container.querySelectorAll<
        Highcharts.HTMLDOMElement | Highcharts.SVGDOMElement
      >(className)) {
        const styles = collectAndRemoveStyles(elem);
        parent.appendChild(elem);
        if (styles.length) elem.setAttribute("style", styles.join(";"));
        elem.style.pointerEvents = "auto";
      }
    }
  };

  // override Highcharts setUpScrolling:
  // - do not use inner-container
  // - use wrapper as scrollingContainer
  // - no more appendChild that breaks on Firefox CSP style attributes
  // https://github.com/highcharts/highcharts/blob/af5fdee7fecda154d0bec897788f17d6ee1aa477/ts/parts/ScrollablePlotArea.ts#L242
  H.Chart.prototype.setUpScrolling = function (): void {
    const container = this.container;
    this.scrollingContainer = getScrollParent(container); // root wrapper element;
    this.scrollingContainer.addEventListener("scroll", () => {
      if (this.pointer) delete this.pointer.chartPosition;
    });
    this.setUpScrolling = null as any;
  };
}
